import { Component, OnInit, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ToastModule } from 'primeng/toast';
import { ConfirmationService, MessageService } from 'primeng/api';
import { catchError, of } from 'rxjs';
import { PlanAdminService } from '../../../core/services/plan-admin.service';
import { AuthService } from '../../../core/services/auth.service';
import { PlanResponse } from '../../../core/models';
import { AppEmptyState } from '../../../shared/ui/empty-state/app-empty-state';
import { FcfaPipe } from '../../../shared/pipes/fcfa.pipe';

@Component({
  selector: 'app-plan-list',
  standalone: true,
  imports: [CommonModule, RouterLink, ButtonModule, ProgressSpinnerModule,
    TooltipModule, ConfirmDialogModule, ToastModule, AppEmptyState, FcfaPipe],
  templateUrl: './plan-list.html',
  styleUrl: './plan-list.scss'
})
export class PlanListComponent implements OnInit {
  private svc     = inject(PlanAdminService);
  private confirm = inject(ConfirmationService);
  private toast   = inject(MessageService);
  authService     = inject(AuthService);

  loading      = signal(true);
  plans        = signal<PlanResponse[]>([]);
  deleting     = signal<string | null>(null);
  featuredPlan = computed(() => this.plans().find(p => p.featured) ?? null);

  ngOnInit() {
    this.load();
  }

  load() {
    this.loading.set(true);
    this.svc.getPlans().pipe(catchError(() => of([]))).subscribe(data => {
      this.plans.set(data);
      this.loading.set(false);
    });
  }

  confirmDelete(plan: PlanResponse, event: Event) {
    this.confirm.confirm({
      target: event.target as EventTarget,
      message: `Supprimer le plan « ${plan.name} » ? Cette action est irréversible.`,
      header: 'Confirmer la suppression',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Supprimer',
      rejectLabel: 'Annuler',
      acceptButtonProps: { severity: 'danger' },
      rejectButtonProps: { severity: 'secondary', outlined: true },
      accept: () => this.delete(plan)
    });
  }

  private delete(plan: PlanResponse) {
    this.deleting.set(plan.id);
    // DELETE renvoie 204 No Content : `null` n'est pas une sentinelle d'erreur fiable ici,
    // Angular résout aussi l'observable avec `null` en cas de succès.
    this.svc.deletePlan(plan.id).subscribe({
      next: () => {
        this.deleting.set(null);
        this.plans.update(p => p.filter(x => x.id !== plan.id));
        this.toast.add({ severity: 'success', summary: 'Plan supprimé', detail: `« ${plan.name} » a été supprimé.`, life: 4000 });
      },
      error: () => {
        this.deleting.set(null);
        this.toast.add({ severity: 'error', summary: 'Erreur', detail: 'La suppression a échoué.', life: 5000 });
      }
    });
  }

  formatProps(n: number) { return n === -1 ? 'Illimité' : n.toString(); }
}
